import React, { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import apiService from '../services/apiService'
import { ArrowLeft, Plus, BookOpen, Play, MoreVertical, ChevronDown, ChevronUp, ChevronRight, MessageSquare, Edit2, Trash2, ArrowUp, ArrowDown, FolderOpen } from 'lucide-react'
import { EditIcon, DeleteIcon } from '../Icons'
import Modal from '../ui/Modal'
import ConfirmModal from '../ui/ConfirmModal'
import AddLesson from './AddLesson'
import EditLesson from './EditLesson'
import Hls from 'hls.js'

// Set to true to re-enable comment functionality in a later phase
const COMMENTS_ENABLED = false

function CourseDetails() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [course, setCourse] = useState(null)
  const videoRef = useRef(null)
  const [lessons, setLessons] = useState([])
  const [categories, setCategories] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [addLessonModalOpen, setAddLessonModalOpen] = useState(false)
  const [editLessonModalOpen, setEditLessonModalOpen] = useState(false)
  const [deleteLessonModalOpen, setDeleteLessonModalOpen] = useState(false)
  const [addCategoryModalOpen, setAddCategoryModalOpen] = useState(false)
  const [editCategoryModalOpen, setEditCategoryModalOpen] = useState(false)
  const [deleteCategoryModalOpen, setDeleteCategoryModalOpen] = useState(false)
  const [categoryToEdit, setCategoryToEdit] = useState(null)
  const [categoryToDelete, setCategoryToDelete] = useState(null)
  const [selectedLessonId, setSelectedLessonId] = useState(null)
  const [selectedLesson, setSelectedLesson] = useState(null)
  const [openMenuId, setOpenMenuId] = useState(null)
  const [defaultCategoryForNewLesson, setDefaultCategoryForNewLesson] = useState(null)
  // Section expand/collapse: key = section.id || 'uncategorized', value = true (expanded) or false (collapsed). Undefined = expanded.
  const [expandedSections, setExpandedSections] = useState({})
  const [addCategoryTitle, setAddCategoryTitle] = useState('')
  const [addCategoryDescription, setAddCategoryDescription] = useState('')
  const [editCategoryTitle, setEditCategoryTitle] = useState('')
  const [editCategoryDescription, setEditCategoryDescription] = useState('')
  const [expandedLessonDescription, setExpandedLessonDescription] = useState(false)
  const [completionUpdate, setCompletionUpdate] = useState(0) // Trigger re-render when lessons are completed
  const [comments, setComments] = useState([])
  const [newComment, setNewComment] = useState('')
  const [isSubmittingComment, setIsSubmittingComment] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [commentToEdit, setCommentToEdit] = useState(null)
  const [editedCommentText, setEditedCommentText] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const menuRefs = useRef({})
  const api = apiService()

  useEffect(() => {
    if (id) {
      fetchCourseDetails()
      fetchLessons()
      fetchCategories()
    }
    // eslint-disable-next-line
  }, [id])

  useEffect(() => {
    if (selectedLesson?._id) {
      fetchComments()
    } else {
      setComments([])
    }
    // eslint-disable-next-line
  }, [selectedLesson?._id])

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openMenuId && menuRefs.current[openMenuId]) {
        if (!menuRefs.current[openMenuId].contains(event.target)) {
          setOpenMenuId(null)
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [openMenuId])

  const fetchCourseDetails = async () => {
    try {
      const response = await api.get(`courses/${id}`)
      if (response.status === 200) {
        setCourse(response.data.course || response.data)
        setIsLoading(false)
      }
    } catch (error) {
      console.error('Error fetching course:', error)
      toast.error('Failed to fetch course details')
      setIsLoading(false)
    }
  }

  const fetchLessons = async () => {
    try {
      const response = await api.get(`courses/${id}/lessons`)
      if (response.status === 200) {
        const lessonsData = response.data.lessons || response.data || []
        setLessons(lessonsData)
        if (lessonsData.length > 0 && !selectedLesson) {
          setSelectedLesson(lessonsData[0])
        }
        return lessonsData
      }
    } catch (error) {
      console.error('Error fetching lessons:', error)
      toast.error('Failed to fetch lessons')
      return []
    }
  }

  const fetchCategories = async () => {
    if (!id) return
    try {
      const response = await api.get(`courses/${id}/categories`)
      if (response.status === 200) {
        setCategories(response.data.categories || [])
      }
    } catch (error) {
      console.error('Error fetching categories:', error)
      setCategories([])
    }
  }

  // Get completed lessons from localStorage (user-specific)
  const getCompletedLessons = () => {
    try {
      const userId = localStorage.getItem('userid')
      const completed = localStorage.getItem(`completedLessons_${id}_${userId}`)
      return completed ? JSON.parse(completed) : []
    } catch (error) {
      console.error('Error reading completed lessons:', error)
      return []
    }
  }

  // Mark a lesson as completed
  const markLessonAsCompleted = (lessonId) => {
    try {
      const userId = localStorage.getItem('userid')
      const completed = getCompletedLessons()
      if (!completed.includes(lessonId)) {
        completed.push(lessonId)
        localStorage.setItem(`completedLessons_${id}_${userId}`, JSON.stringify(completed))
        // Force re-render to update lesson list
        setCompletionUpdate(prev => prev + 1)
      }
    } catch (error) {
      console.error('Error saving completed lesson:', error)
    }
  }

  // Get current user role
  const getCurrentUserRole = () => {
    return localStorage.getItem('role')
  }

  // Format date helper
  const formatDate = (date) => {
    if (!date) return 'N/A'
    const now = new Date()
    const commentDate = new Date(date)
    const diffInSeconds = Math.floor((now - commentDate) / 1000)
    
    if (diffInSeconds < 60) return 'just now'
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minute${Math.floor(diffInSeconds / 60) > 1 ? 's' : ''} ago`
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hour${Math.floor(diffInSeconds / 3600) > 1 ? 's' : ''} ago`
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)} day${Math.floor(diffInSeconds / 86400) > 1 ? 's' : ''} ago`
    if (diffInSeconds < 31536000) return `${Math.floor(diffInSeconds / 2592000)} month${Math.floor(diffInSeconds / 2592000) > 1 ? 's' : ''} ago`
    return `${Math.floor(diffInSeconds / 31536000)} year${Math.floor(diffInSeconds / 31536000) > 1 ? 's' : ''} ago`
  }

  // Fetch comments for selected lesson
  const fetchComments = async () => {
    if (!selectedLesson?._id) return
    try {
      const response = await api.get(`lessons/${selectedLesson._id}/comments`)
      if (response.status === 200) {
        const commentsData = response.data.comments || response.data || []
        setComments(Array.isArray(commentsData) ? commentsData : [])
      }
    } catch (error) {
      console.error('Error fetching comments:', error)
      toast.error('Failed to fetch comments')
      setComments([])
    }
  }

  // Handle submit comment (admin can add comments too)
  const handleSubmitComment = async (e) => {
    e.preventDefault()
    if (!newComment.trim() || !selectedLesson?._id) {
      toast.error('Please enter a comment')
      return
    }

    try {
      setIsSubmittingComment(true)
      const response = await api.post(`lessons/${selectedLesson._id}/comments`, {
        comment: newComment.trim()
      })

      if (response.status === 201) {
        toast.success('Comment added successfully')
        setNewComment('')
        await fetchComments()
      }
    } catch (error) {
      console.error('Error submitting comment:', error)
      toast.error(error.response?.data?.message || 'Failed to add comment')
    } finally {
      setIsSubmittingComment(false)
    }
  }

  // Handle delete comment
  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) {
      return
    }

    try {
      const response = await api.del(`lessons/comments/${commentId}`)
      if (response.status === 200) {
        toast.success('Comment deleted successfully')
        await fetchComments()
      }
    } catch (error) {
      console.error('Error deleting comment:', error)
      toast.error(error.response?.data?.message || 'Failed to delete comment')
    }
  }

  // Handle edit click
  const handleEditClick = (comment) => {
    setCommentToEdit(comment)
    setEditedCommentText(comment.comment)
    setEditModalOpen(true)
  }

  // Handle save edit
  const handleSaveEdit = async () => {
    if (!editedCommentText.trim()) {
      toast.error('Comment cannot be empty')
      return
    }

    try {
      setIsSaving(true)
      const response = await api.put(`admin/comments/${commentToEdit._id}`, {
        comment: editedCommentText.trim()
      })
      
      if (response.status === 200) {
        toast.success('Comment updated successfully')
        setEditModalOpen(false)
        setCommentToEdit(null)
        setEditedCommentText('')
        await fetchComments()
      }
    } catch (error) {
      console.error('Error updating comment:', error)
      toast.error(error.response?.data?.message || 'Failed to update comment')
    } finally {
      setIsSaving(false)
    }
  }

  // Check if current user is admin
  const isAdmin = () => {
    const role = localStorage.getItem('role')
    return role === 'admin' || role === 'subadmin'
  }

  // Check if a lesson is completed
  const isLessonCompleted = (lessonId) => {
    const completed = getCompletedLessons()
    return completed.includes(lessonId)
  }

  const handleLessonClick = (lesson, index) => {
    setSelectedLesson(lesson)
    setExpandedLessonDescription(false) // Reset expanded state when selecting a new lesson
  }

  const toggleLessonDescription = () => {
    setExpandedLessonDescription((prev) => !prev)
  }

  const getVideoUrl = (videoUrl) => {
    if (!videoUrl) return null
    // If it's a local upload, prepend the backend URL
    if (videoUrl.startsWith('/uploads/')) {
      return `${process.env.REACT_APP_BASE_URL?.replace(/\/$/, '')}${videoUrl}`
    }
    // Otherwise, it's an external URL
    return videoUrl
  }

  const isYouTubeUrl = (url) => {
    if (!url) return false
    // eslint-disable-next-line no-useless-escape
    return /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/.test(url)
  }

  const isVimeoUrl = (url) => {
    if (!url) return false
    return /vimeo\.com\/(?:.*\/)?(\d+)/.test(url)
  }

  const isCloudflareStreamUrl = (url) => {
    if (!url) return false
    return /cloudflarestream\.com|videodelivery\.net/.test(url)
  }

  const isCloudflareEmbedUrl = (url) => {
    if (!url) return false
    return /iframe\.videodelivery\.net/.test(url)
  }

  const isHlsUrl = (url) => {
    if (!url) return false
    return /\.m3u8/.test(url)
  }

  const getCloudflareEmbedUrl = (url) => {
    // Extract video ID from Cloudflare Stream URL
    // Format: https://customer-xxxxx.cloudflarestream.com/xxxxx/video.mp4
    // or https://iframe.videodelivery.net/xxxxx
    if (url.includes('videodelivery.net')) {
      const match = url.match(/videodelivery\.net\/([^\/\?]+)/)
      if (match && match[1]) {
        return `https://iframe.videodelivery.net/${match[1]}`
      }
    }
    if (url.includes('cloudflarestream.com')) {
      const match = url.match(/cloudflarestream\.com\/([^\/\?]+)/)
      if (match && match[1]) {
        return `https://iframe.videodelivery.net/${match[1]}`
      }
    }
    return url
  }

  const getYouTubeEmbedUrl = (url) => {
    // eslint-disable-next-line no-useless-escape
    const regExp = /^.*(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/
    const match = url.match(regExp)
    if (match && match[1]) {
      return `https://www.youtube.com/embed/${match[1]}`
    }
    return url
  }

  const getVimeoEmbedUrl = (url) => {
    const regExp = /vimeo\.com\/(?:.*\/)?(\d+)/
    const match = url.match(regExp)
    if (match && match[1]) {
      return `https://player.vimeo.com/video/${match[1]}`
    }
    return url
  }

  // HLS Player Component for Cloudflare HLS streams
  const CloudflareHLSPlayer = ({ src, onEnded }) => {
    const videoElementRef = useRef(null)
    const hlsInstanceRef = useRef(null)

    useEffect(() => {
      const video = videoElementRef.current
      if (!video || !src) return

      // Cleanup previous HLS instance
      if (hlsInstanceRef.current) {
        hlsInstanceRef.current.destroy()
        hlsInstanceRef.current = null
      }

      if (Hls.isSupported()) {
        // Use HLS.js for browsers that don't support native HLS
        const hls = new Hls({
          enableWorker: true,
          lowLatencyMode: false,
        })
        hls.loadSource(src)
        hls.attachMedia(video)
        hlsInstanceRef.current = hls

        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          // Video is ready to play
        })

        hls.on(Hls.Events.ERROR, (event, data) => {
          console.error('HLS error:', data)
          if (data.fatal) {
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                hls.startLoad()
                break
              case Hls.ErrorTypes.MEDIA_ERROR:
                hls.recoverMediaError()
                break
              default:
                hls.destroy()
                break
            }
          }
        })

        return () => {
          if (hlsInstanceRef.current) {
            hlsInstanceRef.current.destroy()
            hlsInstanceRef.current = null
          }
        }
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        // Native HLS support (Safari)
        video.src = src
      } else {
        console.error('HLS is not supported in this browser')
      }
    }, [src])

    return (
      <video
        ref={videoElementRef}
        controls
        className="w-full h-full"
        style={{ maxHeight: '600px' }}
        crossOrigin="anonymous"
        preload="metadata"
        onEnded={onEnded}
        onError={(e) => {
          console.error('HLS video load error:', e)
          console.error('HLS URL:', src)
        }}
      >
        Your browser does not support HLS video playback.
        <p className="text-white p-4">
          If the video doesn't play, check the browser console for errors.
          <br />
          Video URL: {src}
        </p>
      </video>
    )
  }

  const handleDeleteLesson = async (lessonId) => {
    try {
      await api.del(`lessons/${lessonId}`)
      toast.success('Lesson deleted successfully')
      const lessonsData = await fetchLessons()
      // If deleted lesson was selected, select first available lesson
      if (selectedLesson?._id === lessonId && lessonsData && lessonsData.length > 0) {
        const sorted = [...lessonsData].sort((a, b) => (a.order || 0) - (b.order || 0))
        setSelectedLesson(sorted[0])
      } else if (selectedLesson?._id === lessonId) {
        setSelectedLesson(null)
      }
      setDeleteLessonModalOpen(false)
      setSelectedLessonId(null)
    } catch (error) {
      console.error('Error deleting lesson:', error)
      toast.error('Failed to delete lesson')
    }
  }

  const getStatusBadge = (status) => {
    const statusConfig = {
      published: { bg: 'bg-green-100', text: 'text-green-800', label: 'Published' },
      draft: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Draft' },
      archived: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Archived' },
    }
    const config = statusConfig[status] || statusConfig.draft
    return (
      <span
        className={`px-3 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}
      >
        {config.label}
      </span>
    )
  }

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A'
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const toggleMenu = (lessonId, e) => {
    e.stopPropagation()
    setOpenMenuId(openMenuId === lessonId ? null : lessonId)
  }

  const handleMenuEdit = (lessonId, e) => {
    e.stopPropagation()
    setSelectedLessonId(lessonId)
    setEditLessonModalOpen(true)
    setOpenMenuId(null)
  }

  const handleMenuDelete = (lessonId, e) => {
    e.stopPropagation()
    setSelectedLessonId(lessonId)
    setDeleteLessonModalOpen(true)
    setOpenMenuId(null)
  }

  // Group lessons by category (backend returns sorted; we group for display)
  const getSections = () => {
    const uncategorized = lessons.filter((l) => !l.category || !l.category._id)
    const sections = [{ id: null, title: 'Uncategorized', lessons: uncategorized }]
    categories.forEach((cat) => {
      const catLessons = lessons.filter(
        (l) => l.category && (l.category._id === cat._id || l.category === cat._id)
      )
      sections.push({ id: cat._id, title: cat.title, lessons: catLessons })
    })
    return sections
  }

  const handleMoveLesson = async (lesson, direction, sectionLessons) => {
    const idx = sectionLessons.findIndex((l) => l._id === lesson._id)
    if (idx < 0) return
    const newIdx = direction === 'up' ? idx - 1 : idx + 1
    if (newIdx < 0 || newIdx >= sectionLessons.length) return
    const reordered = [...sectionLessons]
    ;[reordered[idx], reordered[newIdx]] = [reordered[newIdx], reordered[idx]]
    const lessonOrders = reordered.map((l, i) => ({ lessonId: l._id, order: i }))
    try {
      await api.put('lessons/reorder', { lessonOrders })
      toast.success('Order updated')
      await fetchLessons()
    } catch (error) {
      console.error('Error reordering:', error)
      toast.error(error.response?.data?.message || 'Failed to reorder')
    }
  }

  const handleCreateCategory = async (e) => {
    e.preventDefault()
    if (!addCategoryTitle.trim()) {
      toast.error('Category title is required')
      return
    }
    try {
      await api.post('categories', {
        title: addCategoryTitle.trim(),
        description: addCategoryDescription.trim() || '',
        courseId: id
      })
      toast.success('Category created')
      setAddCategoryModalOpen(false)
      setAddCategoryTitle('')
      setAddCategoryDescription('')
      await fetchCategories()
    } catch (error) {
      console.error('Error creating category:', error)
      toast.error(error.response?.data?.message || 'Failed to create category')
    }
  }

  const handleUpdateCategory = async (e) => {
    e.preventDefault()
    if (!categoryToEdit || !editCategoryTitle.trim()) return
    try {
      await api.put(`categories/${categoryToEdit._id}`, {
        title: editCategoryTitle.trim(),
        description: editCategoryDescription.trim() || ''
      })
      toast.success('Category updated')
      setEditCategoryModalOpen(false)
      setCategoryToEdit(null)
      await fetchCategories()
    } catch (error) {
      console.error('Error updating category:', error)
      toast.error(error.response?.data?.message || 'Failed to update category')
    }
  }

  const handleDeleteCategory = async () => {
    if (!categoryToDelete || !categoryToDelete.id) return
    try {
      await api.del(`categories/${categoryToDelete.id}`)
      toast.success('Category deleted. Lessons moved to Uncategorized.')
      setDeleteCategoryModalOpen(false)
      setCategoryToDelete(null)
      await fetchCategories()
      await fetchLessons()
    } catch (error) {
      console.error('Error deleting category:', error)
      toast.error(error.response?.data?.message || 'Failed to delete category')
    }
  }

  if (isLoading) {
    return (
      <div className="bg-white border rounded-[10px] border-[#F0F0F0] p-8">
        <div className="text-center">Loading course details...</div>
      </div>
    )
  }

  if (!course) {
    return (
      <div className="bg-white border rounded-[10px] border-[#F0F0F0] p-8">
        <div className="text-center text-red-500">Course not found</div>
      </div>
    )
  }

  const sections = getSections()

  const getSectionKey = (section) => section.id || 'uncategorized'
  const isSectionExpanded = (section) => expandedSections[getSectionKey(section)] !== false
  const toggleSection = (section, e) => {
    if (e) e.stopPropagation()
    const key = getSectionKey(section)
    setExpandedSections((prev) => ({ ...prev, [key]: prev[key] === false }))
  }

  const expandAllSections = () => {
    setExpandedSections({})
  }
  const collapseAllSections = () => {
    const next = {}
    sections.forEach((s) => { next[getSectionKey(s)] = false })
    setExpandedSections(next)
  }

  return (
    <div className="bg-[#FCFCFC] min-h-screen">
      {/* Header */}
      <div className="bg-white border-b border-[#F0F0F0] px-5 py-4 sticky top-0 z-10">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-[#030303]">
            {course.title}
          </h1>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/course-management')}
              className="flex items-center gap-2 px-4 py-2 bg-[#DE3C7D] text-white rounded-md hover:bg-[#c8356f] transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Courses</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content - Udemy Style Layout */}
      <div className="flex flex-col lg:flex-row gap-0">
        {/* Left Side - Video Player Area */}
        <div className="lg:w-2/3 bg-white">
          {selectedLesson ? (
            <div className="p-6">
              {/* Video Player */}
              <div className="bg-black rounded-lg overflow-hidden mb-6" style={{ aspectRatio: '16/9' }}>
                {selectedLesson.videoUrl ? (
                  (() => {
                    const videoUrl = selectedLesson.videoUrl
                    const fullUrl = getVideoUrl(videoUrl)
                    
                    if (isYouTubeUrl(videoUrl)) {
                      return (
                        <iframe
                          key={selectedLesson._id}
                          src={getYouTubeEmbedUrl(videoUrl)}
                          className="w-full h-full"
                          style={{ minHeight: '400px' }}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          title={selectedLesson.title}
                        />
                      )
                    }
                    
                    if (isVimeoUrl(videoUrl)) {
                      return (
                        <iframe
                          key={selectedLesson._id}
                          src={getVimeoEmbedUrl(videoUrl)}
                          className="w-full h-full"
                          style={{ minHeight: '400px' }}
                          frameBorder="0"
                          allow="autoplay; fullscreen; picture-in-picture"
                          allowFullScreen
                          title={selectedLesson.title}
                        />
                      )
                    }
                    
                    // Cloudflare Stream embed URL
                    if (isCloudflareEmbedUrl(videoUrl) || (isCloudflareStreamUrl(videoUrl) && !isHlsUrl(videoUrl) && !fullUrl.includes('.mp4'))) {
                      return (
                        <iframe
                          key={selectedLesson._id}
                          src={getCloudflareEmbedUrl(videoUrl)}
                          className="w-full h-full"
                          style={{ minHeight: '400px' }}
                          frameBorder="0"
                          allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
                          allowFullScreen
                          title={selectedLesson.title}
                        />
                      )
                    }
                    
                    // HLS stream (including Cloudflare HLS)
                    if (isHlsUrl(fullUrl)) {
                      return (
                        <CloudflareHLSPlayer
                          key={selectedLesson._id}
                          src={fullUrl}
                          onEnded={() => {
                            markLessonAsCompleted(selectedLesson._id)
                            toast.success('Lesson completed!')
                          }}
                        />
                      )
                    }
                    
                    // Direct video file URL (including Cloudflare direct MP4)
                    return (
                      <video
                        key={selectedLesson._id}
                        ref={videoRef}
                        controls
                        className="w-full h-full"
                        style={{ maxHeight: '600px' }}
                        crossOrigin="anonymous"
                        preload="metadata"
                        onEnded={() => {
                          markLessonAsCompleted(selectedLesson._id)
                          toast.success('Lesson completed!')
                        }}
                        onError={(e) => {
                          console.error('Video load error:', e)
                          console.error('Video URL:', fullUrl)
                        }}
                      >
                        <source src={fullUrl} type="video/mp4" />
                        <source src={fullUrl} type="video/webm" />
                        <source src={fullUrl} type="video/ogg" />
                        <source src={fullUrl} type="video/quicktime" />
                        <source src={fullUrl} />
                        Your browser does not support the video tag.
                        <p className="text-white p-4">
                          If the video doesn't play, check the browser console for errors.
                          <br />
                          Video URL: {fullUrl}
                        </p>
                      </video>
                    )
                  })()
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white">
                    <div className="text-center">
                      <Play className="w-16 h-16 mx-auto mb-4 opacity-50" />
                      <p className="text-lg">No video available</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Lesson Title and Info */}
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-[#030303] mb-3">
                  {selectedLesson.title}
                </h2>
                {selectedLesson.description && (
                  <div className="mb-4">
                    <p className={`text-[#535862] ${!expandedLessonDescription ? 'line-clamp-2' : ''}`}>
                      {selectedLesson.description}
                    </p>
                    {selectedLesson.description.length > 100 && (
                      <button
                        onClick={toggleLessonDescription}
                        className="text-xs text-[#DE3C7D] hover:text-[#c8356f] font-medium mt-1 flex items-center gap-1 transition-colors"
                      >
                        {expandedLessonDescription ? (
                          <>
                            <span>Show less</span>
                            <ChevronUp className="w-3 h-3" />
                          </>
                        ) : (
                          <>
                            <span>Show more</span>
                            <ChevronDown className="w-3 h-3" />
                          </>
                        )}
                      </button>
                    )}
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 text-sm text-[#535862]">
                    {selectedLesson.videoUrl && (
                      <div className="flex items-center gap-2">
                        <Play className="w-4 h-4" />
                        <span>{formatDuration(selectedLesson.videoDuration)}</span>
                      </div>
                    )}
                    {getStatusBadge(selectedLesson.status)}
                  </div>
                  {selectedLesson.videoUrl && (isYouTubeUrl(selectedLesson.videoUrl) || isVimeoUrl(selectedLesson.videoUrl)) && !isLessonCompleted(selectedLesson._id) && (
                    <button
                      onClick={() => {
                        markLessonAsCompleted(selectedLesson._id)
                        toast.success('Lesson marked as completed!')
                      }}
                      className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors text-sm font-medium flex items-center gap-2"
                    >
                      <span>✓</span>
                      <span>Mark as Complete</span>
                    </button>
                  )}
                </div>
              </div>

              {/* Comments Section - hidden until later phase; set COMMENTS_ENABLED = true to re-enable */}
              {COMMENTS_ENABLED && selectedLesson && (
                <div className="border-t border-gray-200 pt-6 mt-8">
                  {/* Comments Header */}
                  <div className="mb-6">
                    <h3 className="text-xl font-semibold text-[#030303] mb-1">
                      {comments.length} {comments.length === 1 ? 'Comment' : 'Comments'}
                    </h3>
                  </div>

                  {/* Add Comment Form - YouTube Style */}
                  <div className="mb-8">
                    <form onSubmit={handleSubmitComment}>
                      <div className="flex gap-3 items-start">
                        {/* User Avatar */}
                        <div className="w-10 h-10 bg-[#DE3C7D] rounded-full flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
                          {(localStorage.getItem('first_name') || localStorage.getItem('name') || 'U').charAt(0).toUpperCase()}
                        </div>
                        {/* Comment Input */}
                        <div className="flex-1">
                          <div className="relative">
                            <input
                              type="text"
                              value={newComment}
                              onChange={(e) => setNewComment(e.target.value)}
                              placeholder="Add a public comment..."
                              className="w-full px-0 py-2 border-b-2 border-transparent focus:border-[#DE3C7D] focus:outline-none text-sm text-[#030303] placeholder-gray-400 transition-colors"
                              onFocus={(e) => {
                                e.target.style.borderBottomColor = '#DE3C7D'
                              }}
                              onBlur={(e) => {
                                if (!newComment) {
                                  e.target.style.borderBottomColor = 'transparent'
                                }
                              }}
                            />
                          </div>
                          {/* Action Buttons */}
                          {newComment && (
                            <div className="flex justify-end gap-3 mt-3">
                              <button
                                type="button"
                                onClick={() => setNewComment('')}
                                className="px-4 py-2 text-sm font-medium text-[#030303] hover:bg-gray-100 rounded-full transition-colors"
                              >
                                Cancel
                              </button>
                              <button
                                type="submit"
                                disabled={isSubmittingComment || !newComment.trim()}
                                className="px-4 py-2 bg-[#DE3C7D] text-white text-sm font-medium rounded-full hover:bg-[#c8356f] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {isSubmittingComment ? 'Posting...' : 'Comment'}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </form>
                  </div>

                  {/* Comments List - YouTube Style */}
                  <div className="space-y-6">
                    {comments.length === 0 ? (
                      <div className="text-center py-12 bg-white rounded-lg border border-gray-200 p-6">
                        <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                        <p className="text-[#535862] text-lg font-medium">No comments yet. Be the first to comment!</p>
                      </div>
                    ) : (
                      comments.map((comment) => (
                        <div key={comment._id} className="flex gap-3 items-start">
                          {/* User Avatar */}
                          <div className="w-10 h-10 bg-[#DE3C7D] rounded-full flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
                            {comment.student_id?.first_name?.[0] || 'U'}
                          </div>
                          {/* Comment Content */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2 mb-1">
                              <div>
                                <p className="font-medium text-[#030303] text-sm leading-tight">
                                  {comment.student_id?.first_name} {comment.student_id?.last_name}
                                </p>
                                <p className="text-xs text-[#535862] mt-0.5">
                                  {formatDate(comment.createdAt)}
                                </p>
                              </div>
                              {/* Admin actions */}
                              {isAdmin() && (
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => handleEditClick(comment)}
                                    className="text-gray-400 hover:text-[#DE3C7D] transition-colors p-1 flex-shrink-0"
                                    title="Edit comment"
                                  >
                                    <Edit2 className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteComment(comment._id)}
                                    className="text-gray-400 hover:text-red-500 transition-colors p-1 flex-shrink-0"
                                    title="Delete comment"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              )}
                            </div>
                            <p className="text-[#030303] text-sm leading-relaxed whitespace-pre-wrap break-words">
                              {comment.comment}
                            </p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

            </div>
          ) : (
            <div className="p-6">
              <div className="bg-black rounded-lg overflow-hidden mb-6 flex items-center justify-center" style={{ aspectRatio: '16/9', minHeight: '400px' }}>
                <div className="text-center text-white">
                  <BookOpen className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p className="text-lg">Select a lesson to view</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right Side - Course Info and Lesson List */}
        <div className="lg:w-1/3 bg-white border-l border-[#F0F0F0]">
          <div className="p-6 sticky top-[88px] max-h-[calc(100vh-88px)] overflow-y-auto">
            {/* Course Info */}
            <div className="mb-6 pb-6 border-b border-[#F0F0F0]">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm font-semibold text-[#030303]">
                  {course.title}
                </span>
                <span className="text-green-600">✓</span>
              </div>
              <p className="text-xs text-[#535862] mb-3">Course</p>
              <div className="flex items-center gap-4 text-sm text-[#535862]">
                <div className="flex items-center gap-2">
                  <BookOpen className="w-4 h-4" />
                  <span>{lessons.length} Lesson{lessons.length !== 1 ? 's' : ''}</span>
                </div>
              </div>
            </div>

            {/* Course Content: Categories and Lessons */}
            <div>
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-[#030303] mb-3">Course Content</h3>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                  {sections.length > 0 && (
                    <div className="flex items-center gap-1 text-xs">
                      <button
                        type="button"
                        onClick={expandAllSections}
                        className="px-2 py-1 text-[#535862] hover:text-[#DE3C7D] hover:underline rounded"
                      >
                        Expand all
                      </button>
                      <span className="text-[#E0E0E0]" aria-hidden>|</span>
                      <button
                        type="button"
                        onClick={collapseAllSections}
                        className="px-2 py-1 text-[#535862] hover:text-[#DE3C7D] hover:underline rounded"
                      >
                        Collapse all
                      </button>
                    </div>
                  )}
                  <button
                    onClick={() => setAddCategoryModalOpen(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-neutral-100 text-[#030303] rounded-md hover:bg-neutral-200 transition-colors whitespace-nowrap"
                    title="Add section"
                  >
                    <FolderOpen className="w-4 h-4 flex-shrink-0" />
                    <span>Add Category</span>
                  </button>
                  <button
                    onClick={() => { setDefaultCategoryForNewLesson(null); setAddLessonModalOpen(true) }}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-[#DE3C7D] text-white rounded-md hover:bg-[#c8356f] transition-colors whitespace-nowrap"
                  >
                    <Plus className="w-4 h-4 flex-shrink-0" />
                    <span>Add Lesson</span>
                  </button>
                </div>
              </div>
              {lessons.length === 0 && categories.length === 0 ? (
                <div className="text-center py-8 text-[#535862]">
                  <BookOpen className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p className="text-sm mb-2">No categories or lessons yet</p>
                  <p className="text-xs mb-4">Add a category to create sections, then add lessons.</p>
                  <button
                    onClick={() => setAddCategoryModalOpen(true)}
                    className="px-4 py-2 bg-neutral-100 text-[#030303] rounded-md hover:bg-neutral-200 text-sm mr-2"
                  >
                    Add Category
                  </button>
                  <button
                    onClick={() => setAddLessonModalOpen(true)}
                    className="px-4 py-2 bg-[#DE3C7D] text-white rounded-md hover:bg-[#c8356f] transition-colors text-sm"
                  >
                    Add Lesson
                  </button>
                </div>
              ) : (
                <div className="space-y-6">
                  {sections.map((section) => {
                    const sectionKey = getSectionKey(section)
                    const expanded = isSectionExpanded(section)
                    return (
                    <div key={sectionKey} className="border-b border-[#F0F0F0] pb-4 last:border-0">
                      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                        <button
                          type="button"
                          onClick={(e) => toggleSection(section, e)}
                          className="flex items-center gap-1.5 text-left flex-1 min-w-0 group/btn"
                        >
                          {expanded ? (
                            <ChevronDown className="w-4 h-4 text-[#DE3C7D] flex-shrink-0" aria-hidden />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-[#535862] flex-shrink-0" aria-hidden />
                          )}
                          <span className="text-sm font-semibold text-[#030303] truncate">
                            {section.title}
                            {section.lessons.length > 0 && (
                              <span className="text-[#535862] font-normal"> ({section.lessons.length})</span>
                            )}
                          </span>
                        </button>
                        {section.id && (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={(e) => { e.stopPropagation(); setDefaultCategoryForNewLesson(section.id); setAddLessonModalOpen(true) }}
                              className="text-xs text-[#DE3C7D] hover:underline font-medium"
                            >
                              + Lesson
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); const cat = categories.find((c) => c._id === section.id); if (cat) { setCategoryToEdit(cat); setEditCategoryTitle(cat.title); setEditCategoryDescription(cat.description || ''); setEditCategoryModalOpen(true) } }}
                              className="p-1.5 text-[#535862] hover:text-[#DE3C7D] rounded"
                              title="Edit category"
                            >
                              <Edit2 className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); setCategoryToDelete(section); setDeleteCategoryModalOpen(true) }}
                              className="p-1.5 text-[#535862] hover:text-red-500 rounded"
                              title="Delete category"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        )}
                      </div>
                      {expanded && (
                      <div className="space-y-1">
                        {section.lessons.length === 0 ? (
                          <p className="text-xs text-[#535862] py-2">No lessons in this section</p>
                        ) : (
                          section.lessons.map((lesson, index) => {
                            const isSelected = selectedLesson?._id === lesson._id
                            const isCompleted = isLessonCompleted(lesson._id)
                            const userRole = getCurrentUserRole()
                            return (
                              <div
                                key={lesson._id}
                                onClick={() => handleLessonClick(lesson, lessons.findIndex((l) => l._id === lesson._id))}
                                className={`group p-3 rounded-lg transition-all relative flex items-center gap-2 ${
                                  isSelected ? 'bg-[#DE3C7D] text-white cursor-pointer' : 'hover:bg-neutral-50 text-[#030303] cursor-pointer'
                                }`}
                              >
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleMoveLesson(lesson, 'up', section.lessons) }}
                                    disabled={index === 0}
                                    className="p-1 rounded hover:bg-white/20 disabled:opacity-30"
                                    title="Move up"
                                  >
                                    <ArrowUp className="w-3.5 h-3.5" />
                                  </button>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleMoveLesson(lesson, 'down', section.lessons) }}
                                    disabled={index === section.lessons.length - 1}
                                    className="p-1 rounded hover:bg-white/20 disabled:opacity-30"
                                    title="Move down"
                                  >
                                    <ArrowDown className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                                <div className="flex-1 min-w-0 flex items-start gap-2">
                                  {isCompleted && userRole !== 'admin' && userRole !== 'subadmin' && (
                                    <span className="text-green-500 flex-shrink-0 mt-0.5">✓</span>
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <h4 className={`text-sm font-medium truncate ${isSelected ? 'text-white' : 'text-[#030303]'}`}>
                                      {lesson.title}
                                    </h4>
                                    {lesson.videoUrl && (
                                      <div className={`flex items-center gap-1 text-xs ${isSelected ? 'text-white/80' : 'text-[#535862]'}`}>
                                        <Play className="w-3 h-3" />
                                        <span>{formatDuration(lesson.videoDuration)}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <div className="absolute top-2 right-2" ref={(el) => (menuRefs.current[lesson._id] = el)}>
                                  <button
                                    onClick={(e) => toggleMenu(lesson._id, e)}
                                    className={`p-1.5 rounded-md ${isSelected ? 'bg-white/20 hover:bg-white/30 text-white' : 'bg-white/90 hover:bg-white text-[#030303]'} shadow-sm`}
                                    title="More options"
                                  >
                                    <MoreVertical className="w-4 h-4" />
                                  </button>
                                  {openMenuId === lesson._id && (
                                    <div className="absolute top-8 right-0 bg-white rounded-md shadow-lg border border-[#F0F0F0] z-10 min-w-[120px]">
                                      <button
                                        onClick={(e) => handleMenuEdit(lesson._id, e)}
                                        className="w-full px-4 py-2 text-left text-sm text-[#030303] hover:bg-neutral-50 flex items-center gap-2"
                                      >
                                        <EditIcon className="w-4 h-4" />
                                        <span>Edit</span>
                                      </button>
                                      <button
                                        onClick={(e) => handleMenuDelete(lesson._id, e)}
                                        className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                                      >
                                        <DeleteIcon className="w-4 h-4" />
                                        <span>Delete</span>
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )
                          })
                        )}
                      </div>
                      )}
                    </div>
                  )})}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <Modal
        isOpen={addLessonModalOpen}
        onClose={async () => {
          setAddLessonModalOpen(false)
          const lessonsData = await fetchLessons()
          // After fetching, update selected lesson if needed
          if (lessonsData && lessonsData.length > 0 && !selectedLesson) {
            const sorted = [...lessonsData].sort((a, b) => (a.order || 0) - (b.order || 0))
            setSelectedLesson(sorted[0])
          }
        }}
        title="Add Lesson"
      >
        <AddLesson
          courseId={id}
          categories={categories}
          defaultCategoryId={defaultCategoryForNewLesson}
          onClose={async () => {
            setAddLessonModalOpen(false)
            setDefaultCategoryForNewLesson(null)
            const lessonsData = await fetchLessons()
            if (lessonsData && lessonsData.length > 0 && !selectedLesson) setSelectedLesson(lessonsData[0])
          }}
        />
      </Modal>

      <Modal
        isOpen={editLessonModalOpen}
        onClose={async () => {
          const currentLessonId = selectedLessonId
          setEditLessonModalOpen(false)
          setSelectedLessonId(null)
          const lessonsData = await fetchLessons()
          // Update selected lesson after edit
          if (lessonsData && currentLessonId) {
            const sorted = [...lessonsData].sort((a, b) => (a.order || 0) - (b.order || 0))
            const updatedLesson = sorted.find(l => l._id === currentLessonId)
            if (updatedLesson) {
              setSelectedLesson(updatedLesson)
              setExpandedLessonDescription(false) // Reset expanded state after edit
            }
          }
        }}
        title="Edit Lesson"
      >
        <EditLesson
          id={selectedLessonId}
          courseId={id}
          categories={categories}
          onClose={async () => {
            const currentLessonId = selectedLessonId
            setEditLessonModalOpen(false)
            setSelectedLessonId(null)
            const lessonsData = await fetchLessons()
            // Update selected lesson after edit
            if (lessonsData && currentLessonId) {
              const sorted = [...lessonsData].sort((a, b) => (a.order || 0) - (b.order || 0))
              const updatedLesson = sorted.find(l => l._id === currentLessonId)
              if (updatedLesson) {
                setSelectedLesson(updatedLesson)
                setExpandedLessonDescription(false) // Reset expanded state after edit
              }
            }
          }}
        />
      </Modal>

      <ConfirmModal
        isOpen={deleteLessonModalOpen}
        onClose={() => {
          setDeleteLessonModalOpen(false)
          setSelectedLessonId(null)
        }}
        onConfirm={() => handleDeleteLesson(selectedLessonId)}
        title="Delete Lesson"
        message="Are you sure you want to delete this lesson? This action cannot be undone."
      />

      {/* Add Category Modal */}
      <Modal
        isOpen={addCategoryModalOpen}
        onClose={() => { setAddCategoryModalOpen(false); setAddCategoryTitle(''); setAddCategoryDescription('') }}
        title="Add Category"
      >
        <form onSubmit={handleCreateCategory} className="p-4">
          <div className="mb-4">
            <label className="block text-sm font-medium text-[#030303] mb-2">Title *</label>
            <input
              type="text"
              value={addCategoryTitle}
              onChange={(e) => setAddCategoryTitle(e.target.value)}
              className="w-full px-3 py-2 border border-[#F0F0F0] rounded-md focus:outline-none focus:ring-2 focus:ring-[#DE3C7D]"
              placeholder="e.g. Introduction"
              required
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-[#030303] mb-2">Description (optional)</label>
            <textarea
              value={addCategoryDescription}
              onChange={(e) => setAddCategoryDescription(e.target.value)}
              className="w-full px-3 py-2 border border-[#F0F0F0] rounded-md focus:outline-none focus:ring-2 focus:ring-[#DE3C7D]"
              placeholder="Section description"
              rows={2}
            />
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => { setAddCategoryModalOpen(false); setAddCategoryTitle(''); setAddCategoryDescription('') }}
              className="px-4 py-2 border border-[#F0F0F0] rounded-md hover:bg-neutral-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-[#DE3C7D] text-white rounded-md hover:bg-[#c8356f]"
            >
              Create Category
            </button>
          </div>
        </form>
      </Modal>

      {/* Edit Category Modal */}
      <Modal
        isOpen={editCategoryModalOpen}
        onClose={() => { setEditCategoryModalOpen(false); setCategoryToEdit(null) }}
        title="Edit Category"
      >
        <form onSubmit={handleUpdateCategory} className="p-4">
          <div className="mb-4">
            <label className="block text-sm font-medium text-[#030303] mb-2">Title *</label>
            <input
              type="text"
              value={editCategoryTitle}
              onChange={(e) => setEditCategoryTitle(e.target.value)}
              className="w-full px-3 py-2 border border-[#F0F0F0] rounded-md focus:outline-none focus:ring-2 focus:ring-[#DE3C7D]"
              placeholder="Category title"
              required
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-[#030303] mb-2">Description (optional)</label>
            <textarea
              value={editCategoryDescription}
              onChange={(e) => setEditCategoryDescription(e.target.value)}
              className="w-full px-3 py-2 border border-[#F0F0F0] rounded-md focus:outline-none focus:ring-2 focus:ring-[#DE3C7D]"
              placeholder="Section description"
              rows={2}
            />
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => { setEditCategoryModalOpen(false); setCategoryToEdit(null) }}
              className="px-4 py-2 border border-[#F0F0F0] rounded-md hover:bg-neutral-50"
            >
              Cancel
            </button>
            <button type="submit" className="px-4 py-2 bg-[#DE3C7D] text-white rounded-md hover:bg-[#c8356f]">
              Save Changes
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmModal
        isOpen={deleteCategoryModalOpen}
        onClose={() => { setDeleteCategoryModalOpen(false); setCategoryToDelete(null) }}
        onConfirm={handleDeleteCategory}
        title="Delete Category"
        message={categoryToDelete ? `Delete "${categoryToDelete.title}"? Lessons in this section will be moved to Uncategorized.` : 'Delete this category?'}
      />

      {/* Edit Comment Modal - Admin Only (hidden when COMMENTS_ENABLED is false) */}
      {COMMENTS_ENABLED && isAdmin() && (
        <Modal
          isOpen={editModalOpen}
          onClose={() => {
            setEditModalOpen(false)
            setCommentToEdit(null)
            setEditedCommentText('')
          }}
          title="Edit Comment"
        >
          <div className="p-4">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Student: {commentToEdit?.student_id?.first_name} {commentToEdit?.student_id?.last_name}
              </label>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Lesson: {selectedLesson?.title || 'N/A'}
              </label>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Comment
              </label>
              <textarea
                value={editedCommentText}
                onChange={(e) => setEditedCommentText(e.target.value)}
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#DE3C7D] focus:border-transparent"
                placeholder="Enter comment text..."
              />
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setEditModalOpen(false)
                  setCommentToEdit(null)
                  setEditedCommentText('')
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={isSaving || !editedCommentText.trim()}
                className="px-4 py-2 bg-[#DE3C7D] text-white rounded-lg hover:bg-[#c2356a] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}

export default CourseDetails

